import { theme } from '@Guidelines/Theme/theme';
import formatEuros from '@UI/Helpers/formatEuros';
import formatPeriodRange from '@UI/Pages/Administration/AiCosts/Helpers/formatPeriodRange';
import type AiCostSummaryView from '@UI/Store/AiCosts/Views/AiCostSummaryView';
import { css } from '@emotion/react';
import * as React from 'react';

const textStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

type Props = {
	readonly summary: AiCostSummaryView;
};

const pillStyle = (tone: 'danger' | 'success') => css`
	display: inline-flex;
	padding: 0.125rem 0.625rem;
	border-radius: ${theme.radii.pill};
	background-color: ${theme.states[tone].surface};
	color: ${theme.states[tone].ink};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	font-weight: ${theme.fontWeights.semibold};
`;

/** Change against the previous period of the same length; says so when there is no basis to compare. */
const Evolution: React.FC<Props> = ({ summary }) => {
	const { evolutionPercent, previousFrom, previousTo, previousCostAmount } = summary;

	const previous = React.useMemo(
		() =>
			previousFrom !== null && previousTo !== null
				? ` (${formatPeriodRange(previousFrom, previousTo)} : ${formatEuros(previousCostAmount)})`
				: '',
		[previousFrom, previousTo, previousCostAmount],
	);

	if (evolutionPercent === null) {
		return <p css={textStyle}>Nouveau : aucun coût sur la période précédente</p>;
	}

	const rounded = Math.round(Math.abs(evolutionPercent));

	if (rounded === 0) {
		return <p css={textStyle}>{`Stable par rapport à la période précédente${previous}`}</p>;
	}

	const isRise = evolutionPercent > 0;

	return (
		<p css={textStyle}>
			<span css={pillStyle(isRise ? 'danger' : 'success')}>
				{isRise ? `▲ +${rounded} %` : `▼ −${rounded} %`}
			</span>
			{` par rapport à la période précédente${previous}`}
		</p>
	);
};

export default Evolution;
